import MagnifyingGlassIcon from '@heroicons/react/24/outline/MagnifyingGlassIcon.js';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon.js';
import React from 'react';
import { useObjectRef } from 'react-aria';
import { Group, Input as AriaInput, SearchField as AriaSearchField } from 'react-aria-components';

import { FieldAction, FieldActionContext } from '../field/field-action';
import {
  adornmentStyles,
  entryStyles,
  FieldLabel,
  focusEntryOnPress,
  frameState,
  frameStyles,
  useFieldLayout,
} from '../field/field-shell';

interface SearchFieldCommonProps {
  /** The value (controlled). */
  value?: string;
  /** The initial value (uncontrolled). */
  defaultValue?: string;
  /** Called with the new value on every change, and with '' when the search is cleared. */
  onChange?: (value: string) => void;
  /** Called with the value on Enter. Without it Enter is the host's: inside a form, the form submits. */
  onSubmit?: (value: string) => void;
  /** Called when the input loses focus. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Called when the input receives focus. */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Keys pressed in the input. Escape clears a non-empty search before it arrives. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** What the search covers ("Search name or website"). */
  placeholder?: string;
  /** Names the clear button, shown once there is text ("Clear search"). */
  clearLabel: string;
  /** Washed out and skipped by Tab, its clear with it. */
  isDisabled?: boolean;
  /**
   * Height on the Button ramp: md 32px, lg 40px. The clear takes the action
   * seat, which a 24px field has no room for.
   */
  size?: 'md' | 'lg';
  /** The form field name. */
  name?: string;
  /** The id of the input element. */
  id?: string;
  /** Whether the input takes focus on mount. */
  autoFocus?: boolean;
}

interface SearchFieldLabelledProps {
  /** The visible name, above the field or beside it (Form decides). */
  label: string;
  'aria-label'?: never;
}

interface SearchFieldUnlabelledProps {
  label?: never;
  /** The accessible name of a search with no visible label (a table's bar). */
  'aria-label': string;
}

export type SearchFieldProps = SearchFieldCommonProps &
  (SearchFieldLabelledProps | SearchFieldUnlabelledProps);

/* React Aria stops every key at a search field, which would kill a host's
 * shortcut (Cmd+K to the global search) while typing here. Every key travels
 * on from here; the input decides Escape itself, per event, because React
 * Aria's continuePropagation flag lives in the handler's closure and would
 * otherwise carry from one key to the next until a re-render. */
const letKeysThrough = (e: { continuePropagation: () => void }) => e.continuePropagation();

/**
 * A search input: the magnifying glass at its start, a clear at its end once
 * there is text, and Escape clearing the text before it reaches the host (an
 * empty search lets Escape through, so a dialog still closes). The same frame
 * and ramp as TextField, without a message line, a read-only state,
 * `autoComplete` or `inputMode`: a search filters what is on screen, it never
 * holds a value to keep or a rule to meet, `type="search"` already picks the
 * keyboard, and a filter has nothing to autofill. What it covers is its
 * placeholder.
 *
 * The forwarded ref is the `<input>`.
 */
export const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField(
    {
      label,
      clearLabel,
      isDisabled = false,
      size = 'md',
      placeholder,
      onBlur,
      onFocus,
      onKeyDown,
      onSubmit,
      ...props
    },
    ref,
  ) {
    const inputRef = useObjectRef(ref);
    const { labelPosition, styles } = useFieldLayout(size);
    const { root, label: labelClass, column } = styles;
    const { icon } = adornmentStyles({ size });
    const seat = React.useMemo(() => ({ size, isDisabled, isTransient: true }), [size, isDisabled]);
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      /* React Aria has just cleared this text: keep the key from the host, so
       * a dialog closes on the next Escape, not this one. The DOM still holds
       * the text here; the state is what was emptied. */
      if (e.key === 'Escape' && e.currentTarget.value !== '') e.stopPropagation();
      /* Submit here rather than through React Aria, which has no guard for
       * Enter confirming an IME candidate. */
      if (e.key === 'Enter' && onSubmit && !e.nativeEvent.isComposing) {
        e.preventDefault();
        onSubmit(e.currentTarget.value);
      }
      onKeyDown?.(e);
    };

    return (
      <AriaSearchField
        {...props}
        isDisabled={isDisabled}
        validationBehavior="aria"
        onKeyDown={letKeysThrough}
        className={root()}
        data-component="search-field"
        data-label-position={labelPosition}
      >
        {({ isEmpty }) => (
          <>
            {label ? (
              <FieldLabel isRequired={false} className={labelClass()}>
                {label}
              </FieldLabel>
            ) : null}
            <div className={column()}>
              <Group
                className={frameStyles({
                  size,
                  multiline: false,
                  hasAction: !isEmpty && !isDisabled,
                  state: frameState({ isDisabled, isReadOnly: false, isInvalid: false }),
                })}
                onMouseDown={focusEntryOnPress(inputRef)}
              >
                <MagnifyingGlassIcon aria-hidden="true" className={icon()} />
                {/* On the Input, as TextField does: React DOM events for the
                 * caller, chained after React Aria's own key handling. */}
                <AriaInput
                  ref={inputRef}
                  className={entryStyles({ size, multiline: false, search: true })}
                  placeholder={placeholder}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  onKeyDown={handleKeyDown}
                />
                {/* React Aria hands the clear its press, its name's fallback
                 * and its place outside the tab order through the button
                 * context; the seat only sizes it and names it. */}
                {isEmpty || isDisabled ? null : (
                  <FieldActionContext.Provider value={seat}>
                    <FieldAction icon={XMarkIcon} label={clearLabel} />
                  </FieldActionContext.Provider>
                )}
              </Group>
            </div>
          </>
        )}
      </AriaSearchField>
    );
  },
);
